import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Pagination,
  Alert,
  InputAdornment,
  Grid,
  Chip,
  Collapse,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { 
  InfoOutlined, 
  Edit, 
  Delete, 
  FilterList,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, parseISO, startOfToday, subDays, startOfMonth } from 'date-fns';
import { useTransactions } from '../context/TransactionContext';
import { formatCurrency } from '../utils/formatters';

// Default categories
const CATEGORIES = [
  'Coffee', 
  'Lunch', 
  'Dinner', 
  'Food', 
  'Groceries', 
  'Gas', 
  'Snacks', 
  'Clothes', 
  'Entertainment', 
  'Transportation', 
  'Utilities', 
  'Other',
  'Uncategorized'
];

const TransactionsPage = () => {
  const { 
    transactions, 
    pagination,
    filters,
    loading, 
    error, 
    setError,
    fetchTransactions,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    applyFilters,
    changePage
  } = useTransactions();
  
  // State for filtering and pagination
  const [showFilters, setShowFilters] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // State for edit/delete dialogs
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  
  // Edit form state
  const [editAmount, setEditAmount] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editCustomCategory, setEditCustomCategory] = useState('');
  const [editDate, setEditDate] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editError, setEditError] = useState('');
  
  // Note tooltip state
  const [noteTooltip, setNoteTooltip] = useState({
    open: false,
    id: null
  });
  
  // Quick filter date ranges
  const [dateRange, setDateRange] = useState('all');
  
  // Load transactions on component mount
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);
  
  // Apply filters when they change
  const handleApplyFilters = () => {
    const newFilters = {
      startDate: filterStartDate,
      endDate: filterEndDate,
      category: filterCategory || null,
      sortBy,
      sortDirection
    };
    
    applyFilters(newFilters);
  };
  
  // Reset filters
  const handleResetFilters = () => {
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterCategory('');
    setSortBy('date');
    setSortDirection('desc');
    setDateRange('all');
    
    applyFilters({
      startDate: null,
      endDate: null,
      category: null,
      sortBy: 'date',
      sortDirection: 'desc'
    });
  };
  
  // Handle date range selection
  const handleDateRangeChange = (event, newRange) => {
    if (newRange === null) return;
    
    setDateRange(newRange);
    
    const today = startOfToday();
    let start = null;
    let end = null;
    
    switch (newRange) {
      case 'today':
        start = today;
        end = today;
        break;
      case '7days':
        start = subDays(today, 7);
        end = today;
        break;
      case '30days':
        start = subDays(today, 30);
        end = today;
        break;
      case 'month':
        start = startOfMonth(today);
        end = today;
        break;
      default:
        // 'all' - no filtering
        break;
    }
    
    setFilterStartDate(start);
    setFilterEndDate(end);
  };
  
  // Handle edit dialog open
  const handleEditOpen = (transaction) => {
    setCurrentTransaction(transaction);
    setEditAmount(transaction.amount.toString());
    setEditCategory(transaction.category);
    setEditDate(new Date(transaction.date));
    setEditNote(transaction.note || '');
    setEditError('');
    
    // Check if it's a custom category
    if (!CATEGORIES.includes(transaction.category)) {
      setEditCategory('custom');
      setEditCustomCategory(transaction.category);
    } else {
      setEditCustomCategory('');
    }
    
    setEditDialogOpen(true);
  };
  
  // Handle edit dialog close
  const handleEditClose = () => {
    setEditDialogOpen(false);
    setCurrentTransaction(null);
  };
  
  // Handle delete dialog open
  const handleDeleteOpen = (transaction) => {
    setCurrentTransaction(transaction);
    setDeleteDialogOpen(true);
  };
  
  // Handle delete dialog close
  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setCurrentTransaction(null);
  };
  
  // Handle save edit
  const handleSaveEdit = async () => {
    setEditError('');
    
    // Form validation
    if (!editAmount.trim()) {
      setEditError('Please enter an amount');
      return;
    }
    
    const amountValue = parseFloat(editAmount);
    if (isNaN(amountValue) || amountValue <= 0) {
      setEditError('Please enter a valid amount greater than 0');
      return;
    }
    
    // Determine the final category value
    let finalCategory = 'Uncategorized';
    
    if (editCategory === 'custom' && editCustomCategory.trim()) {
      finalCategory = editCustomCategory.trim();
    } else if (editCategory) {
      finalCategory = editCategory;
    }
    
    try {
      await updateTransaction(currentTransaction._id, {
        amount: amountValue,
        category: finalCategory,
        date: editDate,
        note: editNote.trim()
      });
      
      handleEditClose();
    } catch (error) {
      setEditError('Failed to update transaction: ' + error.message);
    }
  };
  
  // Handle confirm delete
  const handleConfirmDelete = async () => {
    try {
      await deleteTransaction(currentTransaction._id);
      handleDeleteClose();
    } catch (error) {
      setError('Failed to delete transaction: ' + error.message);
      handleDeleteClose();
    }
  };
  
  // Handle note tooltip toggle
  const handleNoteTooltipToggle = (id) => {
    if (noteTooltip.open && noteTooltip.id === id) {
      setNoteTooltip({ open: false, id: null });
    } else {
      setNoteTooltip({ open: true, id });
    }
  };
  
  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3, position: 'relative' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Transactions
          </Typography>
        </Box>
        <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
          <IconButton onClick={() => setShowFilters(!showFilters)}>
            <FilterList />
          </IconButton>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {/* Filters Section */}
      <Collapse in={showFilters}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Date Range
              </Typography>
              <ToggleButtonGroup
                value={dateRange}
                exclusive
                onChange={handleDateRangeChange}
                aria-label="date range"
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="today">Today</ToggleButton>
                <ToggleButton value="7days">Last 7 Days</ToggleButton>
                <ToggleButton value="30days">Last 30 Days</ToggleButton>
                <ToggleButton value="month">This Month</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={filterStartDate}
                  onChange={setFilterStartDate}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  maxDate={filterEndDate || new Date()}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={filterEndDate}
                  onChange={setFilterEndDate}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                  maxDate={new Date()}
                  minDate={filterStartDate}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="filter-category-label">Category</InputLabel>
                <Select
                  labelId="filter-category-label"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="">
                    <em>All Categories</em>
                  </MenuItem>
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="sort-by-label">Sort By</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="amount">Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="sort-direction-label">Order</InputLabel>
                <Select
                  labelId="sort-direction-label"
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                  label="Order"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handleResetFilters} color="inherit">
              Reset
            </Button>
            <Button onClick={handleApplyFilters} variant="contained">
              Apply Filters
            </Button>
          </Box>
        </Paper>
      </Collapse>
      
      {/* Transactions List */}
      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : transactions.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No transactions found.
            </Typography>
            <Button 
              variant="outlined" 
              sx={{ mt: 2 }}
              onClick={handleResetFilters}
            >
              Clear Filters
            </Button>
          </Box>
        ) : (
          <>
            <List disablePadding>
              {transactions.map((transaction, index) => (
                <React.Fragment key={transaction._id}>
                  {index > 0 && <Divider component="li" />}
                  <ListItem sx={{ py: 2 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body1" component="span" fontWeight="medium">
                            {formatCurrency(transaction.amount)}
                          </Typography>
                          {transaction.note && (
                            <Tooltip
                              title={transaction.note}
                              open={noteTooltip.open && noteTooltip.id === transaction._id}
                              onClose={() => setNoteTooltip({ open: false, id: null })}
                              arrow
                            >
                              <IconButton 
                                size="small" 
                                onClick={() => handleNoteTooltipToggle(transaction._id)}
                                sx={{ ml: 1 }}
                              >
                                <InfoOutlined fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" component="span" color="text.secondary">
                            {format(new Date(transaction.date), 'MMM d, yyyy')}
                          </Typography>
                          <Chip 
                            label={transaction.category} 
                            size="small" 
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleEditOpen(transaction)}>
                        <Edit />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteOpen(transaction)}>
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
            
            {/* Pagination */}
            {pagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination 
                  count={pagination.pages} 
                  page={pagination.page} 
                  onChange={(e, page) => changePage(page)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>
      
      {/* Edit Transaction Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} fullWidth maxWidth="sm">
        <DialogTitle>Edit Transaction</DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          
          <TextField
            margin="dense"
            label="Amount"
            type="number"
            fullWidth
            required
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ mb: 2 }}
          />
          
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="edit-category-label">Category</InputLabel>
            <Select
              labelId="edit-category-label"
              value={editCategory}
              onChange={(e) => {
                setEditCategory(e.target.value);
                if (e.target.value !== 'custom') {
                  setEditCustomCategory('');
                }
              }}
              label="Category"
            >
              <MenuItem value="">
                <em>Uncategorized</em>
              </MenuItem>
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom Category</MenuItem>
            </Select>
          </FormControl>
          
          {editCategory === 'custom' && (
            <TextField
              margin="dense"
              label="Custom Category Name"
              fullWidth
              required
              value={editCustomCategory}
              onChange={(e) => setEditCustomCategory(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={editDate}
              onChange={setEditDate}
              maxDate={new Date()} // Don't allow future dates
              renderInput={(params) => <TextField {...params} fullWidth margin="dense" sx={{ mb: 2 }} />}
            />
          </LocalizationProvider>
          
          <TextField
            margin="dense"
            label="Note (Optional)"
            fullWidth
            multiline
            rows={2}
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
          />
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this transaction?
          </Typography>
          {currentTransaction && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Amount:</strong> {formatCurrency(currentTransaction.amount)}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {currentTransaction.category}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {format(new Date(currentTransaction.date), 'MMM d, yyyy')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionsPage;